// WrongSiteExcludedPanel - files removed as the wrong site: by a person ("Mark
// wrong-site", or confirming a quarantine) or by the wrong-site check's own confident
// verdict. These are settled removals, not guesses awaiting review, so this is a
// plain "Reinstate any that belong here" list, deliberately separate from the
// QuarantinedFilesPanel review queue. A check's removal is labelled as such: agents
// may reverse those themselves, a person's they may not.
import { useContext, useState } from "react";

import { useReinstateWrongSite, useUploadsList, type UploadRecord } from "../api";
import { UserContext } from "../auth/UserContext";
import { SightingList } from "./SightingList";

const WRONG_SITE_REASON = "operator_excluded_wrong_site";

interface Props {
  projectId: string;
}

function displayName(originalName: string): string {
  return originalName.replace(/\s\[[^\]]+\]\.json$/, "");
}

function formatWhen(at?: string): string {
  if (!at) return "";
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

export const WrongSiteExcludedPanel = ({ projectId }: Props): JSX.Element | null => {
  const { data: uploads } = useUploadsList(projectId);
  const { user } = useContext(UserContext);
  const reinstate = useReinstateWrongSite(projectId);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const excluded = (uploads ?? []).filter(
    (u) => u.status === "removed" && u.removal_reason === WRONG_SITE_REASON,
  );
  if (excluded.length === 0) return null;

  const onReinstate = async (u: UploadRecord) => {
    setError(null);
    setBusyId(u.upload_id);
    try {
      await reinstate.mutateAsync({ upload_id: u.upload_id, user_email: user?.email ?? undefined });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    // Plain, not an alert box: this is a standing list of settled decisions with
    // a Reinstate affordance, not a warning an operator has to clear. It also
    // sits inside the "Excluded files" group now, which supplies the card chrome.
    <div style={{ fontSize: 13, color: "#3f3f46" }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        Excluded as wrong site ({excluded.length})
      </div>
      <div style={{ marginBottom: 8, color: "#71717a", fontSize: 12 }}>
        {excluded.length === 1 ? "This file was" : "These files were"} excluded as belonging to a
        different site, by a person or by the automated wrong-site check.{" "}
        {excluded.length === 1 ? "It is" : "They are"} hidden from the project and the agents.
        Reinstate any that belong here.
      </div>
      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {excluded.map((u) => (
          <li
            key={u.upload_id}
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              justifyContent: "space-between",
              // A generated PV report name runs to 100+ characters with no
              // spaces; without this it pushes the whole card wider than the
              // page instead of wrapping.
              minWidth: 0,
            }}
          >
            <span
              style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}
            >
              <span style={{ fontWeight: 500, overflowWrap: "anywhere" }}>
                {displayName(u.original_name)}
              </span>
              <span style={{ fontSize: 11, color: "#71717a", overflowWrap: "anywhere" }}>
                {u.wrong_site_exclusion?.detected_other_site
                  ? `Looks like: ${u.wrong_site_exclusion.detected_other_site} · `
                  : ""}
                {u.removed_by_gate
                  ? "removed by the automated check"
                  : `excluded${u.wrong_site_exclusion?.by ? ` by ${u.wrong_site_exclusion.by}` : ""}`}
                {formatWhen(u.wrong_site_exclusion?.at)
                  ? ` · ${formatWhen(u.wrong_site_exclusion?.at)}`
                  : ""}
              </span>
              <span style={{ fontSize: 11, color: "#b91c1c" }}>
                <SightingList sightings={u.sightings ?? []} />
              </span>
            </span>
            <button
              type="button"
              onClick={() => void onReinstate(u)}
              disabled={busyId !== null}
              style={{
                flexShrink: 0,
                fontSize: 12,
                padding: "4px 10px",
                borderRadius: 6,
                border: "1px solid #dc2626",
                background: "#fff",
                color: "#991b1b",
                cursor: busyId !== null ? "default" : "pointer",
              }}
            >
              {busyId === u.upload_id ? "…" : "Reinstate"}
            </button>
          </li>
        ))}
      </ul>
      {error && (
        <div style={{ marginTop: 8, color: "#991b1b", fontSize: 12 }}>
          Could not reinstate the file: {error}
        </div>
      )}
    </div>
  );
};
